'use client';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertTriangle, CheckCircle, ChevronDown, CircleAlert, Info } from 'lucide-react';
import { ReactNode, useState } from 'react';

export interface CustomAlertProps {
  variant: 'error' | 'warning' | 'success' | 'info';
  title: string;
  description: ReactNode;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  collapsible?: boolean;
  defaultCollapsed?: boolean;
}

const iconSizeClasses = {
  sm: '!h-4 !w-4',
  md: '!h-5 !w-5',
  lg: '!h-5 !w-5 scale-110 -translate-x-1.5',
};

const textSizeClasses = {
  sm: 'text-sm',
  md: 'text-base',
  lg: 'text-lg',
};

export default function CustomAlert({
  variant,
  title,
  description,
  className = '',
  size = 'sm',
  collapsible = true,
  defaultCollapsed = false,
}: CustomAlertProps) {
  const [isCollapsed, setIsCollapsed] = useState(collapsible && defaultCollapsed);

  const getVariantConfig = () => {
    const iconSize = iconSizeClasses[size];
    const textSize = textSizeClasses[size];
    const baseClassName = `text-left bg-transparent shadow ${textSize} ${className}`;

    switch (variant) {
      case 'error':
        return {
          icon: <AlertTriangle className={`!text-red-500 ${iconSize}`} />,
          className: `${baseClassName} border-red-500/20 text-muted-foreground`,
        };
      case 'warning':
        return {
          icon: <CircleAlert className={`!text-orange-500 ${iconSize}`} />,
          className: `${baseClassName} border-orange-500/20`,
        };
      case 'info':
        return {
          icon: <Info className={`!text-cyan-500 ${iconSize}`} />,
          className: `${baseClassName} border-cyan-500/20`,
        };
      case 'success':
        return {
          icon: <CheckCircle className={`!text-green-500 ${iconSize}`} />,
          className: `${baseClassName} border-green-500/20`,
        };
      default:
        return {
          icon: <Info className={`!text-blue-500 ${iconSize}`} />,
          className: `${baseClassName} border-blue-500/20`,
        };
    }
  };

  const config = getVariantConfig();

  return (
    <Alert variant="default" className={config.className}>
      {config.icon}
      <div className="col-start-2 w-full">
        <div className="flex items-center justify-between gap-2">
          <AlertTitle className="flex-1">{title}</AlertTitle>
          {collapsible && (
            <button
              type="button"
              onClick={() => setIsCollapsed(v => !v)}
              className="text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
              aria-label={isCollapsed ? 'Expand' : 'Collapse'}
            >
              <ChevronDown
                className={`transition-transform duration-300 ease-in-out ${iconSizeClasses[size]} ${
                  isCollapsed ? 'rotate-0' : 'rotate-180'
                }`}
              />
            </button>
          )}
        </div>
        <div
          className={`overflow-hidden transition-all duration-300 ease-in-out ${
            isCollapsed ? 'max-h-0 opacity-0' : 'max-h-[2000px] opacity-100'
          }`}
        >
          <AlertDescription className="w-full mt-2">{description}</AlertDescription>
        </div>
      </div>
    </Alert>
  );
}

